import React, { useState, useEffect } from 'react';
import { 
  Mail, TrendingUp, AlertTriangle, MousePointer, Eye, 
  RefreshCw, Search, XCircle, CheckCircle, Loader2, ExternalLink,
  BarChart3, Users, Ban, Clock
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { API_URL } from '../../utils/api';

export function AdminEmailAnalyticsTab() {
  const [stats, setStats] = useState(null);
  const [bounces, setBounces] = useState([]);
  const [messages, setMessages] = useState([]);
  const [suppressions, setSuppressions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview'); // overview, messages, bounces, suppressions
  const [periodDays, setPeriodDays] = useState('30');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  
  // Message detail dialog
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messageDetail, setMessageDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    loadData();
  }, [periodDays]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load stats
      const statsRes = await fetch(`${API_URL}/api/email-analytics/stats?days=${periodDays}`, {
        headers: getAuthHeaders()
      });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      // Load recent messages
      const messagesRes = await fetch(`${API_URL}/api/email-analytics/messages?count=50`, {
        headers: getAuthHeaders()
      });
      if (messagesRes.ok) {
        const data = await messagesRes.json();
        setMessages(data.messages || []);
      }

      // Load bounces
      const bouncesRes = await fetch(`${API_URL}/api/email-analytics/bounces?count=50`, {
        headers: getAuthHeaders()
      });
      if (bouncesRes.ok) {
        const data = await bouncesRes.json();
        setBounces(data.bounces || []);
      }

      // Load suppressions
      const suppRes = await fetch(`${API_URL}/api/email-analytics/suppressions?count=100`, {
        headers: getAuthHeaders()
      });
      if (suppRes.ok) {
        const data = await suppRes.json();
        setSuppressions(data.suppressions || []);
      }
    } catch (error) {
      console.error('Error loading email analytics:', error);
      toast.error('Failed to load email analytics');
    } finally {
      setLoading(false);
    }
  };

  const searchEmailStatus = async () => {
    if (!searchEmail.trim()) return;
    
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/api/email-analytics/lookup?email=${encodeURIComponent(searchEmail)}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setSearchResults(await res.json());
      } else {
        toast.error('Failed to search email');
      }
    } catch (error) {
      console.error('Error searching email:', error);
      toast.error('Failed to search email');
    } finally {
      setSearching(false);
    }
  };

  const loadMessageDetail = async (messageId) => {
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API_URL}/api/email-analytics/messages/${messageId}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setMessageDetail(await res.json());
      }
    } catch (error) {
      console.error('Error loading message detail:', error);
      toast.error('Failed to load message details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const reactivateBounce = async (bounceId) => {
    try {
      const res = await fetch(`${API_URL}/api/email-analytics/bounces/${bounceId}/activate`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Email address reactivated');
        loadData();
      } else {
        toast.error('Failed to reactivate');
      }
    } catch (error) {
      toast.error('Failed to reactivate');
    }
  };

  const removeSuppression = async (email) => {
    try {
      const res = await fetch(`${API_URL}/api/email-analytics/suppressions/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Removed from suppression list');
        loadData();
      } else {
        toast.error('Failed to remove');
      }
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Email Analytics</h2>
          <p className="text-sm text-slate-500">Monitor email delivery, opens, clicks, and bounces via Postmark</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={periodDays} onValueChange={setPeriodDays}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'messages', label: 'Messages', icon: Mail },
          { id: 'bounces', label: 'Bounces', icon: AlertTriangle },
          { id: 'suppressions', label: 'Suppressed', icon: Ban }
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeView === tab.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveView(tab.id)}
            className="gap-1"
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.id === 'bounces' && bounces.length > 0 && (
              <Badge variant="destructive" className="ml-1 text-xs">{bounces.length}</Badge>
            )}
          </Button>
        ))}
      </div>

      {/* Overview View */}
      {activeView === 'overview' && stats && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats.sent?.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">Emails Sent</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats.delivery_rate}%</p>
                    <p className="text-xs text-slate-500">Delivery Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-100 rounded-lg">
                    <Eye className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats.open_rate}%</p>
                    <p className="text-xs text-slate-500">Open Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <MousePointer className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats.click_rate}%</p>
                    <p className="text-xs text-slate-500">Click Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Stats */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Engagement Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Total Opens</span>
                    <span className="font-medium">{stats.opens?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Unique Opens</span>
                    <span className="font-medium">{stats.unique_opens?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Total Clicks</span>
                    <span className="font-medium">{stats.clicks?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Unique Clicks</span>
                    <span className="font-medium">{stats.unique_clicks?.toLocaleString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Delivery Issues</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Bounced</span>
                    <Badge variant={stats.bounced > 0 ? 'destructive' : 'secondary'}>
                      {stats.bounced}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Spam Complaints</span>
                    <Badge variant={stats.spam_complaints > 0 ? 'destructive' : 'secondary'}>
                      {stats.spam_complaints}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Suppressed Emails</span>
                    <Badge variant={suppressions.length > 0 ? 'outline' : 'secondary'}>
                      {suppressions.length}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Email Lookup */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Search className="w-4 h-4" />
                Email Status Lookup
              </CardTitle>
              <CardDescription>Check delivery status for a specific email address</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter email address..."
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchEmailStatus()}
                  className="max-w-md"
                />
                <Button onClick={searchEmailStatus} disabled={searching}>
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Search
                </Button>
              </div>

              {searchResults && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-medium">{searchResults.email}</span>
                    {searchResults.is_suppressed ? (
                      <Badge variant="destructive">Suppressed - {searchResults.suppression_reason}</Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-green-100 text-green-700">Active</Badge>
                    )}
                  </div>
                  
                  {searchResults.recent_messages?.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-600">Recent messages:</p>
                      {searchResults.recent_messages.map((msg, i) => (
                        <div key={i} className="flex items-center justify-between text-sm p-2 bg-white rounded border">
                          <div>
                            <span className="font-medium">{msg.subject}</span>
                            <span className="text-slate-500 ml-2">{formatDate(msg.sent_at)}</span>
                          </div>
                          <Badge variant={msg.status === 'Sent' ? 'secondary' : 'outline'}>{msg.status}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No recent messages found</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Messages View */}
      {activeView === 'messages' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Outbound Messages</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {messages.map((msg) => (
                  <TableRow key={msg.message_id}>
                    <TableCell className="font-mono text-xs">{msg.to?.[0] || '-'}</TableCell>
                    <TableCell className="max-w-xs truncate">{msg.subject}</TableCell>
                    <TableCell>
                      <Badge variant={msg.status === 'Sent' ? 'secondary' : 'outline'}>
                        {msg.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{formatDate(msg.sent_at)}</TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setSelectedMessage(msg);
                          loadMessageDetail(msg.message_id);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Bounces View */}
      {activeView === 'bounces' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Bounced Emails
            </CardTitle>
            <CardDescription>Emails that failed to deliver</CardDescription>
          </CardHeader>
          <CardContent>
            {bounces.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p>No bounces! All emails delivered successfully.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bounces.map((bounce) => (
                    <TableRow key={bounce.id}>
                      <TableCell className="font-mono text-xs">{bounce.email}</TableCell>
                      <TableCell>
                        <Badge variant={bounce.type === 'HardBounce' ? 'destructive' : 'outline'}>
                          {bounce.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-slate-600">
                        {bounce.description}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDate(bounce.bounced_at)}</TableCell>
                      <TableCell>
                        {bounce.can_activate && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => reactivateBounce(bounce.id)}
                          >
                            Reactivate
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Suppressions View */}
      {activeView === 'suppressions' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-500" />
              Suppressed Emails
            </CardTitle>
            <CardDescription>Emails blocked from receiving future messages (hard bounces, spam complaints)</CardDescription>
          </CardHeader>
          <CardContent>
            {suppressions.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p>No suppressed emails</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Origin</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suppressions.map((supp, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-xs">{supp.email}</TableCell>
                      <TableCell>
                        <Badge variant="destructive">{supp.suppression_reason}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{supp.origin}</TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDate(supp.created_at)}</TableCell>
                      <TableCell>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => removeSuppression(supp.email)}
                          className="text-red-600 hover:text-red-700"
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Message Detail Dialog */}
      <Dialog open={!!selectedMessage} onOpenChange={() => { setSelectedMessage(null); setMessageDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Message Details</DialogTitle>
            <DialogDescription>{selectedMessage?.subject}</DialogDescription>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : messageDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">To:</span>
                  <span className="ml-2 font-mono">{messageDetail.to?.[0]}</span>
                </div>
                <div>
                  <span className="text-slate-500">Status:</span>
                  <Badge className="ml-2" variant="secondary">{messageDetail.status}</Badge>
                </div>
                <div>
                  <span className="text-slate-500">Sent:</span>
                  <span className="ml-2">{formatDate(messageDetail.sent_at)}</span>
                </div>
              </div>

              {/* Opens */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-4 h-4 text-violet-600" />
                  <span className="font-medium">Opens: {messageDetail.opens?.total || 0}</span>
                </div>
                {messageDetail.opens?.events?.length > 0 && (
                  <div className="space-y-1 text-sm">
                    {messageDetail.opens.events.map((open, i) => (
                      <div key={i} className="flex justify-between text-slate-600">
                        <span>{formatDate(open.opened_at)}</span>
                        <span>{open.client} on {open.os}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Clicks */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <MousePointer className="w-4 h-4 text-amber-600" />
                  <span className="font-medium">Clicks: {messageDetail.clicks?.total || 0}</span>
                </div>
                {messageDetail.clicks?.events?.length > 0 && (
                  <div className="space-y-1 text-sm">
                    {messageDetail.clicks.events.map((click, i) => (
                      <div key={i} className="flex justify-between text-slate-600">
                        <span>{formatDate(click.clicked_at)}</span>
                        <span className="truncate max-w-xs">{click.url}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminEmailAnalyticsTab;
